"use client";

import React from "react";

import { useTranslations } from "next-intl";

import { cn } from "@/utils/cn";

export interface PropertyAgentInfoProps {
  /** CSS class name for custom styling */
  className?: string;
  /** Property ID */
  propertyId: string;
  /** Currently assigned agent */
  assignedAgent?: {
    id: string;
    name: string;
    avatar?: string;
  };
  /** Current user */
  currentUser?: {
    id: string;
    name: string;
  };
  /** Whether the current user can assign properties */
  canAssign?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Show assignment action */
  showAssignAction?: boolean;
}

/**
 * PropertyAgentInfo - Property agent assignment info according to Figma design
 *
 * Figma Design Specifications (1-25):
 * - Container: 361.54×19px
 * - Assignment text: font-size: 15px, font-weight: 500, color: #373737
 * - Action button: font-size: 15px, font-weight: 500, color: #373737
 * - Font Family: Geometria
 * - Alignment: left text, right action button
 */
export const PropertyAgentInfo: React.FC<PropertyAgentInfoProps> = ({
  className,
  propertyId,
  assignedAgent,
  currentUser,
  canAssign = true,
  loading = false,
  showAssignAction = true,
}) => {
  const t = useTranslations();
  const tRealtor = useTranslations("realtor");

  const isAssignedToCurrentUser = assignedAgent?.id === currentUser?.id;

  console.log("PropertyAgentInfo debug:", {
    assignedAgent: assignedAgent?.name,
    currentUser: currentUser?.name,
    isAssignedToCurrentUser
  });

  return (
    <div
      className={cn(
        "flex items-center justify-between",
        "w-full max-w-[361px] h-[19px]", // Exact dimensions from Figma
        className,
      )}
      data-testid="property-agent-info"
    >
      {/* Left part: assignment information */}
      <div className="flex items-center">
        <span
          className={cn(
            "text-[15px] font-medium text-[#373737] leading-[19px]", // Exact typography from Figma
            "font-[family-name:Geometria]", // Figma font family
          )}
        >
          {assignedAgent ? (
            <>
              {tRealtor("assignmentAssignedToLabel")}:{" "}
              <span className="font-medium">{assignedAgent.name}</span>
            </>
          ) : (
            tRealtor("assignmentNotAssignedText")
          )}
        </span>
      </div>

      {/* Right part: "Assign to me" action */}
      {showAssignAction && canAssign && !isAssignedToCurrentUser && (
        <button
          type="button"
          className={cn(
            "text-[15px] font-medium text-[#373737] leading-[19px]", // Exact typography from Figma
            "font-[family-name:Geometria]", // Figma font family
            "hover:text-[#CF1409] hover:underline", // Hover state
            "transition-colors duration-200",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "flex-shrink-0", // Prevents button shrinking
          )}
          onClick={() => {
            // Assignment logic will be handled internally in the wrapper
            console.log('[PropertyAgentInfo] Assign button clicked - logic moved to wrapper');
          }}
          disabled={loading}
          data-testid="assign-to-me-button"
        >
          {loading
            ? tRealtor("assignmentAssigningText")
            : tRealtor("assignmentAssignToMeText")}
        </button>
      )}

      {/* Show current assignment info if property is assigned to current user */}
      {isAssignedToCurrentUser && (
        <span
          className={cn(
            "text-[15px] font-medium text-[#CF1409] leading-[19px]", // Red color for highlighting
            "font-[family-name:Geometria]",
            "flex-shrink-0",
          )}
        >
          {tRealtor("assignmentAssignedToMeText")}
        </span>
      )}
    </div>
  );
};

export default PropertyAgentInfo;
